import React, { useState, useEffect } from "react";
import { Modal, Input, Button, message } from "antd";
import axiosInstance from "../../service/AxiosInstance";
import { fetchPreviousFeedbacks } from "../../screens/portfolioApp/portfolio/commonApis";
import { useDrawer } from "../layout-components/menu/menuContext";
import "./report-modal.css";

const ReportFeedbackModal = ({ 
  reportData = {}, 
  existingFeedback = [], 
  loadingFeedback = false, 
  onFeedbackRefresh 
}) => {
  const { state, dispatch } = useDrawer();
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 1000;

  // Function to handle feedback text change with character limit
  const handleFeedbackTextChange = (e) => {
    const newText = e.target.value;
    
   //- check limit and return early if exceeded
    if (newText.length > MAX_CHARS) {
      message.warning(`Feedback cannot exceed ${MAX_CHARS} characters.`);
      return; // Don't update the state - prevents typing
    }
    
    setFeedbackText(newText);
    setCharCount(newText.length);
  };

  // Function to ensure feedback is loaded
  const ensureFeedbackLoaded = async () => {
    // If feedback is already loaded or currently loading, don't fetch again
    if (state.previousFeedbacks.length > 0 || state.loadingFeedbacks) {
      return;
    }

    try {
      dispatch({ type: "SET_LOADING_FEEDBACKS", value: true });
      const feedbacks = await fetchPreviousFeedbacks();
      dispatch({ 
        type: "SET_PREVIOUS_FEEDBACKS", 
        value: feedbacks 
      });
    } catch (error) {
      console.error("Error loading feedbacks:", error);
      dispatch({ type: "SET_PREVIOUS_FEEDBACKS", value: [] });
    } finally {
      dispatch({ type: "SET_LOADING_FEEDBACKS", value: false });
    }
  };

  // Helper function to format dealer name with city and state
  const formatDealerLocation = (dealerData) => {
    if (!dealerData?.dealer_name) return '';
    
    const locationParts = [];
    if (dealerData.dealer_city) locationParts.push(dealerData.dealer_city);
    if (dealerData.dealer_state) locationParts.push(dealerData.dealer_state);

    if (locationParts.length > 0) {
      return `${dealerData.dealer_name}, ${locationParts.join(', ')}`;
    }
    
    return dealerData.dealer_name;
  };

  // Generate context text for visual display only (not saved with feedback)
  const getContextDisplay = () => {
    if (!reportData?.dealer_name) return null;
    
    // Check if it's a valuation page (add "Dealership:" prefix and location)
    // For portfolio/other pages, dealer_name already contains formatted text like "Filtered Search - ..." or "Portfolio Directory"
    const isValuationPage = reportData.report_type === 'Valuation' || reportData.dealer_info_id;
    
    if (isValuationPage && !reportData.dealer_name.includes('Directory')) {
      const dealerDisplay = formatDealerLocation(reportData);
      return `Dealership: ${dealerDisplay}`;
    }
    
    // For portfolio pages, return as-is (already formatted)
    return reportData.dealer_name;
  };

  // Calculate context display text once for performance
  const contextDisplayText = getContextDisplay();

  useEffect(() => {
    // Listen for custom event from navbar button
    const handleOpenFeedback = async () => {
      // Ensure feedback is loaded before opening modal
      await ensureFeedbackLoaded();
      setFeedbackModalVisible(true);
    };

    window.addEventListener('openFeedbackModal', handleOpenFeedback);

    return () => {
      window.removeEventListener('openFeedbackModal', handleOpenFeedback);
    };
  }, [reportData, state.previousFeedbacks, state.loadingFeedbacks]);

  const handleFeedbackSave = async () => {
    try {
      setSavingFeedback(true);
      
      // Validate feedback text
      if (!feedbackText.trim()) {
        message.warning("Please enter your feedback before saving");
        return;
      }

      // Validate required report data
      if (!reportData?.dealer_name || !reportData?.report_type) {
        message.error("Missing report information. Cannot save feedback.");
        console.error("Missing report data:", reportData);
        return;
      }

      // Format dealershipname field with dealer location
      let dealershipNameField;
      if (reportData.dealer_info_id) {
        // For valuation pages - include city and state
        const dealerNameWithLocation = formatDealerLocation(reportData);
        dealershipNameField = `Dealer: ${dealerNameWithLocation}`;
      } else {
        // For portfolio searches or pages without dealer info
        dealershipNameField = reportData.dealer_name;
      }

      // Prepare API payload
      const payload = {
        dealershipname: dealershipNameField,
        reporttype: reportData.report_type,
        feedback: feedbackText.trim(),
      };

      // Make API call to report-feedback endpoint
      const response = await axiosInstance.post("/report-feedback", payload);
      
      message.success("Feedback saved successfully!");
      
      // Close the modal immediately after successful save
      handleFeedbackModalClose();
      
      try {
        dispatch({ type: "SET_LOADING_FEEDBACKS", value: true });
        const feedbacks = await fetchPreviousFeedbacks();
        dispatch({ 
          type: "SET_PREVIOUS_FEEDBACKS", 
          value: feedbacks 
        });
      } catch (error) {
        console.error("Error refreshing feedbacks:", error);
      } finally {
        dispatch({ type: "SET_LOADING_FEEDBACKS", value: false });
      }
      
      // Also call the passed refresh function if available (for backward compatibility)
      if (onFeedbackRefresh) {
        await onFeedbackRefresh();
      }
      
    } catch (error) {
      console.error("Error saving feedback:", error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.data?.error || "Failed to save feedback";
        message.error(`Error: ${errorMessage}`);
      } else if (error.request) {
        // Request was made but no response received
        message.error("Network error. Please check your connection and try again.");
      } else {
        // Something else happened
        message.error("An unexpected error occurred while saving feedback.");
      }
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleFeedbackModalClose = () => {
    setFeedbackModalVisible(false);
    // Clear the feedback text and character count when modal is closed without saving
    setFeedbackText("");
    setCharCount(0);
  };

  // Function to format feedback data for display
  const formatFeedbackForDisplay = () => {
    const feedbackToUse = state.previousFeedbacks || existingFeedback || [];
    
    if (!feedbackToUse || feedbackToUse.length === 0) {
      return "No existing feedback found.";
    }

    return feedbackToUse
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Sort by newest first
      .map((feedback, index) => {
        const date = new Date(feedback.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        return `[${date}] ${feedback.dealershipname} \n${feedback.feedback}\n`;
      })
      .join('\n');
  };

  return (
    <Modal
      open={feedbackModalVisible}
      onCancel={handleFeedbackModalClose}
      footer={null}
      width={600}
      className="feedback-modal"
      closable={false}
      destroyOnClose={true}
    >
      <div className="feedback-content">
        <p className="feedback-description">
          Please provide feedback on this Screen. Let us know which sections you find most useful and what additional information you would like to see included. You can reopen this notepad at any time while reviewing the screen. Thank you!
        </p>
        
        {/* Combined container with non-editable context at top and editable textarea below */}
        <div className="feedback-input-wrapper">
          {contextDisplayText && (
            <div className="feedback-context-inside">
              {contextDisplayText}
            </div>
          )}
          <Input.TextArea
            value={feedbackText}
            onChange={handleFeedbackTextChange}
            rows={6}
            className="feedback-textarea-with-context"
            placeholder="Enter your feedback here..."
          />
        </div>
        
        <div className="existing-feedback-section">
          <p className="existing-feedback-label">Previous Feedback:</p>
          <Input.TextArea
            value={(state.loadingFeedbacks || loadingFeedback) ? "Loading existing feedback..." : formatFeedbackForDisplay()}
            readOnly
            rows={8}
            className="existing-feedback-textarea"
          />
        </div>
        
        <div className="feedback-buttons">
          <Button
            onClick={handleFeedbackModalClose}
          >
            Back
          </Button>
          <Button
            type="primary"
            onClick={handleFeedbackSave}
            // loading={savingFeedback}
            disabled={savingFeedback || charCount > MAX_CHARS}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportFeedbackModal; 